import React from 'react'
import PropTypes from 'prop-types'
import { currencyFormatUS } from '@helpers/string-helper'
import { Stack, Grid, Button, Typography, styled, css } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useProduct } from '../hooks'
import PriceAtom from '../../../@rtg2022/components/atoms/Price/Price'
import FailuresImage from '../../../assets/images/esc/FAILURES.svg'
import RipsImage from '../../../assets/images/esc/RIPS_CUTS.svg'
import FoodBevImage from '../../../assets/images/esc/FOOD_BEVERAGE.svg'
import BreakageImage from '../../../assets/images/esc/BREAKAGE.svg'
import StrainsImage from '../../../assets/images/esc/STAINS.svg'
import BurnsImage from '../../../assets/images/esc/BURNS.svg'

const WarrantyInfoStyled = styled('div')(
  ({ theme }) => css`
    .WarrantyInfo {
      &__Actions {
        margin: 10px 0 0 0;
      }
      &__RemoveWarrantyWrapper {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        white-space: pre;
      }
      &__CircleIcon {
        margin-right: 10px;
      }
      &__PlanText {
        ${theme.breakpoints.down('md')} {
          font-size: 14px;
        }
        font-size: 18px;
        font-weight: 500;
        color: ${theme.palette.success.main};
      }
      &__PlanTextMargin {
        margin-right: 20px;
      }
      &__IconButton {
        font-size: 16px;
        font-weight: 600;
        // fix button having 50% radius
        border-radius: 4px;
      }
      .MuiSvgIcon-root {
        margin-right: 10px;
      }
    }
    &__RemoveWarranty {
      font-size: 14px;
      text-transform: none;
      font-weight: 400;
      color: ${theme.palette.text.main};
    }

    ${theme.breakpoints.up('md')} {
      &__Actions {
        margin: 5px 0 22px 0;
      }
    }
  `,
)

const WarrantyInfoTitle = styled('div')(() => ({
  paddingBottom: '15px',
}))
const WarrantyCoverage = styled('div')(({ theme }) => ({
  backgroundColor: `${theme.palette.primary.main}08`,
  padding: '15px',
}))
const CoverageText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
}))

const WarrantyPrice = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  columnGap: '6px',
  padding: '0 15px',
  fontSize: '24px',
  [theme.breakpoints.down('xs')]: {
    padding: '15px 0',
  },
}))

const IconContainer = styled('div')(() => ({
  width: '36px',
  minWidth: '36px',
}))

const ItemContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  columnGap: '5px',
  marginRight: 'auto',
}))

const ItemText = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.dark,
  fontWeight: 500,
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
  },
}))

const AddWarrantyContainer = styled(Stack)(() => ({}))

const StyledButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  [theme.breakpoints.down('xs')]: {
    width: '100%',
    fontSize: '14px',
  },
}))

const StyledRemoveButton = styled(Button)(() => ({
  border: 'none',
  fontWeight: 600,
}))

const gridItems = [
  {
    img: FoodBevImage,
    alt: 'Food and Beverage',
    text: 'Food/Beverage',
  },
  {
    img: StrainsImage,
    alt: 'Stains',
    text: 'Stains',
  },
  {
    img: BurnsImage,
    alt: 'Burns',
    text: 'Burns',
  },
  {
    img: BreakageImage,
    alt: 'Breakage',
    text: 'Breakage',
  },
  {
    img: RipsImage,
    alt: 'Rips and Cuts',
    text: 'Rips/Cuts',
  },
  {
    img: FailuresImage,
    alt: 'Failures',
    text: 'Failures',
  },
]

const WarrantyInfo = props => {
  const { price, isWarrantyEnabled, onToggleWarranty, showWarrantyModal, product, isMobile } = props
  const { financeNumMonths, showFinance } = useProduct({ product }, [])
  const monthlyPayment = price / financeNumMonths
  return (
    <WarrantyInfoStyled className="WarrantyInfo">
      <>
        <WarrantyInfoTitle>
          <Typography fontSize={isMobile ? 16 : 19}>Three (3) Years Furniture Protection Plan</Typography>
        </WarrantyInfoTitle>
        <WarrantyCoverage>
          <CoverageText fontSize={isMobile ? 16 : 19} textTransform="uppercase" fontWeight={500}>
            Coverage at a Glance
          </CoverageText>
          <Typography mb={2} fontSize={isMobile ? 14 : 16}>
            Protect your purchase against accidental damage and defects{' '}
            <span>
              <button
                aria-describedby="warrantyPlpDesc"
                type="button"
                onClick={showWarrantyModal}
                onKeyDown={e => {
                  if (e.key === 'space' || e.key === 'enter') {
                    showWarrantyModal()
                  }
                }}
                className="info-link fake-link"
              >
                Learn More
              </button>
            </span>
          </Typography>
          <Grid container columnSpacing={isMobile ? 4 : 12} rowSpacing={2} justifyContent="space-between">
            {gridItems.map((item, i) => (
              <Grid item key={item.alt} xs={isMobile ? 6 : 4}>
                <ItemContainer>
                  <IconContainer>
                    <img src={item.img} alt={item.alt} />
                  </IconContainer>
                  <ItemText>{item.text}</ItemText>
                </ItemContainer>
              </Grid>
            ))}
          </Grid>
        </WarrantyCoverage>
      </>
      {isMobile ? (
        <>
          <WarrantyPrice>
            <PriceAtom price={currencyFormatUS(price)} />
            {showFinance && (
              <span style={{ fontSize: '18px', fontWeight: 400 }}>
                (<PriceAtom price={monthlyPayment} /> per month)
              </span>
            )}
          </WarrantyPrice>

          {isWarrantyEnabled ? (
            <>
              <div className="WarrantyInfo__RemoveWarrantyWrapper">
                <CheckCircleOutlineIcon color="success" className="WarrantyInfo__CircleIcon" />
                <span className="WarrantyInfo__PlanText">Protection </span>
                <span className="WarrantyInfo__PlanText">Plan </span>
                <span className="WarrantyInfo__PlanText WarrantyInfo__PlanTextMargin">Added</span>
                <StyledRemoveButton
                  variant="text"
                  className="WarrantyInfo__RemoveWarranty"
                  onClick={() => onToggleWarranty(!isWarrantyEnabled)}
                >
                  Remove
                </StyledRemoveButton>
              </div>
            </>
          ) : (
            <StyledButton
              variant="contained"
              onClick={() => onToggleWarranty(!isWarrantyEnabled)}
              className="WarrantyInfo__IconButton"
              color="primary"
              type="button"
            >
              ADD PROTECTION
            </StyledButton>
          )}
        </>
      ) : (
        <AddWarrantyContainer
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          className="WarrantyInfo__Actions"
        >
          <WarrantyPrice>
            <PriceAtom price={currencyFormatUS(price)} />
            {showFinance && (
              <span style={{ fontSize: '18px', fontWeight: 400 }}>
                (<PriceAtom price={monthlyPayment} /> per month)
              </span>
            )}
          </WarrantyPrice>

          {isWarrantyEnabled ? (
            <>
              <div className="WarrantyInfo__RemoveWarrantyWrapper">
                <CheckCircleOutlineIcon color="success" className="WarrantyInfo__CircleIcon" />
                <span className="WarrantyInfo__PlanText">Protection </span>
                <span className="WarrantyInfo__PlanText">Plan </span>
                <span className="WarrantyInfo__PlanText WarrantyInfo__PlanTextMargin">Added</span>
                <StyledRemoveButton
                  variant="text"
                  className="WarrantyInfo__RemoveWarranty"
                  onClick={() => onToggleWarranty(!isWarrantyEnabled)}
                >
                  Remove
                </StyledRemoveButton>
              </div>
            </>
          ) : (
            <StyledButton
              variant="contained"
              onClick={() => onToggleWarranty(!isWarrantyEnabled)}
              className="WarrantyInfo__IconButton"
              color="primary"
              type="button"
            >
              ADD PROTECTION
            </StyledButton>
          )}
        </AddWarrantyContainer>
      )}
    </WarrantyInfoStyled>
  )
}

WarrantyInfo.propTypes = {
  price: PropTypes.number,
  isWarrantyEnabled: PropTypes.bool,
  onToggleWarranty: PropTypes.func,
  showWarrantyModal: PropTypes.func,
  product: PropTypes.object,
  isMobile: PropTypes.bool,
}

export default WarrantyInfo
